import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import styles from './Posts.module.css';

interface Post {
  id: number;
  title: string;
  content: string;
  isPinned: boolean;
  createdAt: string;
  postImageUrl?: string;
  videoUrl?: string;
  isVideoPost?: boolean;
}

const Posts: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get('posts');
        setPosts(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const openPostModal = (post: Post) => setSelectedPost(post);
  const closePostModal = () => setSelectedPost(null);

  const renderMediaPreview = (post: Post) => {
    if (post.isVideoPost && post.videoUrl) {
      return (
        <div className={styles.videoWrapper}>
          <iframe
            src={post.videoUrl.replace('watch?v=', 'embed/')}
            title="Video Post"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            width="100%"
            height="315"
          />
        </div>
      );
    } else if (post.postImageUrl) {
      return (
        <img src={post.postImageUrl} alt="Post" className={styles.postImage} />
      );
    }
    return null;
  };

  if (loading) {
    return <div className={styles.loading}>Loading posts...</div>;
  }

  const pinnedPosts = posts.filter(p => p.isPinned);
  const regularPosts = posts.filter(p => !p.isPinned);

  return (
    <div className={styles.postsContainer}>
      <h1 className={styles.pageTitle}>Latest News & Updates</h1>

      {pinnedPosts.length > 0 && (
        <div className={styles.pinnedSection}>
          <h2 className={styles.sectionTitle}>📌 Pinned Announcements</h2>
          <div className={styles.postsGrid}>
            {pinnedPosts.map(post => (
              <div key={post.id} className={`${styles.postCard} ${styles.pinnedCard}`} onClick={() => openPostModal(post)}>
                {renderMediaPreview(post)}
                <div className={styles.postContent}>
                  <h3>{post.title}</h3>
                  <p className={styles.timestamp}>
                    {new Date(post.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.regularSection}>
        <h2 className={styles.sectionTitle}>All Posts</h2>
        {regularPosts.length === 0 ? (
          <p className={styles.emptyState}>No posts available</p>
        ) : (
          <div className={styles.postsGrid}>
            {regularPosts.map(post => (
              <div key={post.id} className={styles.postCard} onClick={() => openPostModal(post)}>
                {renderMediaPreview(post)}
                <div className={styles.postContent}>
                  <h3>{post.title}</h3>
                  <p className={styles.timestamp}>
                    {new Date(post.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPost && (
        <div className={styles.modalOverlay} onClick={closePostModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={closePostModal}>&times;</button>
            <h2>{selectedPost.title}</h2>

            {selectedPost.isVideoPost && selectedPost.videoUrl ? (
              <div className={styles.videoContainer}>
                <iframe
                  width="100%"
                  height="400"
                  src={selectedPost.videoUrl.replace("watch?v=", "embed/")}
                  title="Video Post"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : selectedPost.postImageUrl ? (
              <img
                src={selectedPost.postImageUrl}
                alt="Full Post"
                className={styles.modalImage}
              />
            ) : null}

            <div
              className={styles.postBody}
              dangerouslySetInnerHTML={{ __html: selectedPost.content }}
            />

            <p className={styles.timestamp}>
              Posted on {new Date(selectedPost.createdAt).toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Posts;
